import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Footer from "@/components/Footer";
import { Calendar, Clock, ArrowRight, ChevronLeft } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useTranslation();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [translations, setTranslations] = useState([]); // language siblings
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  // Update SEO meta tags directly in DOM when post loads
  useEffect(() => {
    if (post) {
      // SEO golden rule: <title> tag MUST equal <h1>.
      // Both point to the same canonical headline (post.title), suffixed
      // with brand for the <title> tag to compete in SERP.
      document.title = `${post.title} | Chatity`;
      
      // Helper function to update or create meta tag
      const updateMetaTag = (selector, attribute, value) => {
        let tag = document.querySelector(selector);
        if (!tag) {
          tag = document.createElement('meta');
          if (attribute === 'property') {
            tag.setAttribute('property', selector.match(/\[property="([^"]+)"\]/)[1]);
          } else {
            tag.setAttribute('name', selector.match(/\[name="([^"]+)"\]/)[1]);
          }
          document.head.appendChild(tag);
        }
        tag.content = value;
      };
      
      // Update meta description
      updateMetaTag('meta[name="description"]', 'name', post.seo_description || post.excerpt || '');
      
      // Update or create keywords
      if (post.seo_keywords) {
        updateMetaTag('meta[name="keywords"]', 'name', post.seo_keywords);
      }
      
      // Update Open Graph tags — use post.title (= H1) as canonical headline
      updateMetaTag('meta[property="og:title"]', 'property', post.title);
      updateMetaTag('meta[property="og:description"]', 'property', post.seo_description || post.excerpt || '');
      updateMetaTag('meta[property="og:type"]', 'property', 'article');
      updateMetaTag('meta[property="og:url"]', 'property', `https://chatity.io/blog/${post.slug}`);
      // og:locale per post language (en_US / fr_FR / ru_RU)
      const ogLocaleMap = { en: 'en_US', fr: 'fr_FR', ru: 'ru_RU' };
      updateMetaTag('meta[property="og:locale"]', 'property', ogLocaleMap[post.language] || 'en_US');
      
      if (post.featured_image) {
        updateMetaTag('meta[property="og:image"]', 'property', `https://chatity.io${post.featured_image}`);
      }
      
      // <html lang="..."> for proper language signalling
      if (post.language) {
        document.documentElement.setAttribute('lang', post.language);
      }

      // Update canonical link
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = `https://chatity.io/blog/${post.slug}`;
      
      console.log('✅ SEO meta tags updated from database:', {
        title: post.seo_title,
        description: post.seo_description?.substring(0, 50) + '...',
        keywords: post.seo_keywords,
        language: post.language,
      });
    }
  }, [post]);

  // Render hreflang <link rel="alternate"> tags for language siblings
  useEffect(() => {
    // Remove ALL existing hreflang <link> tags (both the generic ones injected by
    // SEOHead/HreflangTags for the landing page AND any previously injected
    // dynamic ones). We replace them with slug-aware alternates for this post.
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    if (!post || !translations || translations.length === 0) return;
    if (translations.length <= 1) return; // no point in hreflang for a single-language post
    translations.forEach(sib => {
      if (!sib.language || !sib.slug) return;
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', sib.language);
      link.setAttribute('href', `https://chatity.io/blog/${sib.slug}`);
      link.setAttribute('data-dyn', 'blog-hreflang');
      document.head.appendChild(link);
    });
    // x-default → English version if available, else first
    const enSib = translations.find(s => s.language === 'en') || translations[0];
    if (enSib) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', 'x-default');
      link.setAttribute('href', `https://chatity.io/blog/${enSib.slug}`);
      link.setAttribute('data-dyn', 'blog-hreflang');
      document.head.appendChild(link);
    }
  }, [post, translations]);

  // Inject Schema.org BlogPosting + Twitter Card meta when post loads
  useEffect(() => {
    if (!post) return;

    const updateOrCreateMeta = (selector, attrName, attrValue, content) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attrName, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    const url = `https://chatity.io/blog/${post.slug}`;
    const imageUrl = post.featured_image
      ? (post.featured_image.startsWith('http') ? post.featured_image : `https://chatity.io${post.featured_image}`)
      : 'https://chatity.io/og-default.png';

    // ── Twitter Card ──────────────────────────────────────────────
    updateOrCreateMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    updateOrCreateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', post.title);
    updateOrCreateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', post.seo_description || post.excerpt || '');
    updateOrCreateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);
    updateOrCreateMeta('meta[name="twitter:url"]', 'name', 'twitter:url', url);

    // ── Article meta for Open Graph ───────────────────────────────
    updateOrCreateMeta('meta[property="article:published_time"]', 'property', 'article:published_time',
      post.published_date || post.created_at || '');
    updateOrCreateMeta('meta[property="article:modified_time"]', 'property', 'article:modified_time',
      post.updated_at || post.published_date || '');
    updateOrCreateMeta('meta[property="article:author"]', 'property', 'article:author', post.author || 'Chatity Team');
    updateOrCreateMeta('meta[property="article:section"]', 'property', 'article:section', 'AI Chatbots');

    // ── Schema.org BlogPosting JSON-LD ────────────────────────────
    document.querySelectorAll('script[data-schema="blog-posting"]').forEach(el => el.remove());
    const wordCount = (post.content || '').replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      'headline': post.title,
      'name': post.title,
      'description': post.seo_description || post.excerpt || '',
      'image': [imageUrl],
      'datePublished': post.published_date || post.created_at,
      'dateModified': post.updated_at || post.published_date || post.created_at,
      'author': {
        '@type': 'Organization',
        'name': post.author || 'Chatity Team',
        'url': 'https://chatity.io',
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Chatity',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://chatity.io/logo.png',
        },
      },
      'mainEntityOfPage': { '@type': 'WebPage', '@id': url },
      'url': url,
      'inLanguage': post.language || 'en',
      'keywords': post.seo_keywords || '',
      'wordCount': wordCount,
      'articleSection': 'AI Chatbots',
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.schema = 'blog-posting';
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
  }, [post]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
      
      // Fetch related posts + language translations in parallel
      const [relatedResponse, translationsResponse] = await Promise.allSettled([
        axios.get(`${API}/blog/posts/${slug}/related`),
        axios.get(`${API}/blog/posts/${slug}/translations`),
      ]);
      if (relatedResponse.status === 'fulfilled') {
        setRelatedPosts(relatedResponse.value.data.posts || []);
      }
      if (translationsResponse.status === 'fulfilled') {
        setTranslations(translationsResponse.value.data.translations || []);
      }
    } catch (error) {
      console.error("Error fetching blog post:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-US', options);
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">{t('blog.notFound')}</h1>
          <Link to="/blog" className="text-black underline hover:no-underline">
            {t('blog.backToBlog')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-white text-black">
        {/* Header */}
        <header className="border-b-2 border-black">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-header.png" alt="Chatity" className="h-8 w-auto" />
            </Link>
            
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Link to="/auth">
                <button className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium">
                  {t('landing.login')}
                </button>
              </Link>
            </div>
          </div>
        </header>

        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-black">{t('landing.home')}</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-black">{t('blog.title')}</Link>
            <span>/</span>
            <span className="text-black font-medium line-clamp-1">{post.title}</span>
          </div>
        </div>

        {/* Back to Blog */}
        <div className="container mx-auto px-4 py-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-black hover:gap-3 transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>{t('blog.backToBlog')}</span>
          </Link>
        </div>

        {/* Article */}
        <article className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Featured Image */}
          {post.featured_image && (
            <div className="mb-8 rounded-xl overflow-hidden border-2 border-black">
              <img 
                src={post.featured_image} 
                alt={post.title}
                title={post.title}
                width="1200"
                height="500"
                loading="eager"
                decoding="async"
                className="w-full aspect-[12/5] object-cover"
              />
            </div>
          )}

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-8 pb-8 border-b-2 border-gray-200">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{formatDate(post.published_date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{calculateReadTime(post.content)} {t('blog.minRead')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-black text-white rounded-full text-sm font-medium">
                {post.author}
              </span>
            </div>
          </div>

          {/* Content (sanitized against XSS) */}
          <div 
            className="prose prose-lg max-w-none blog-content"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          />

          {/* CTA Section */}
          <div className="mt-16 p-8 bg-black text-white rounded-xl text-center">
            <h3 className="text-2xl font-bold mb-4">
              {t('blog.ctaTitle')}
            </h3>
            <p className="text-gray-300 mb-6">
              {t('blog.ctaDescription')}
            </p>
            <Link to="/auth">
              <button className="px-8 py-4 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors font-bold text-lg">
                {t('blog.ctaButton')}
              </button>
            </Link>
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="container mx-auto px-4 py-16 border-t-2 border-gray-200">
            <h2 className="text-3xl font-bold mb-8">{t('blog.relatedPosts')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link 
                  key={relatedPost.id} 
                  to={`/blog/${relatedPost.slug}`}
                  className="group"
                >
                  <article className="border-2 border-black rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300">
                    {relatedPost.featured_image ? (
                      <div className="aspect-video bg-gray-100 overflow-hidden">
                        <img 
                          src={relatedPost.featured_image} 
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-black to-gray-800 flex items-center justify-center">
                        <div className="text-white text-6xl font-bold opacity-20">C</div>
                      </div>
                    )}
                    
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 group-hover:text-gray-700 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      
                      <div className="flex items-center gap-2 text-black font-medium mt-4 group-hover:gap-3 transition-all">
                        <span>{t('blog.readMore')}</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Blog Content Styles */}
        <style>{`
          .blog-content {
            font-size: 1.125rem;
            line-height: 1.8;
            color: #1a1a1a;
          }
          
          .blog-content h2 {
            font-size: 2rem;
            font-weight: 700;
            margin-top: 3rem;
            margin-bottom: 1.5rem;
            color: #000;
          }
          
          .blog-content h3 {
            font-size: 1.5rem;
            font-weight: 600;
            margin-top: 2rem;
            margin-bottom: 1rem;
            color: #000;
          }
          
          .blog-content p {
            margin-bottom: 1.5rem;
          }
          
          .blog-content ul, .blog-content ol {
            margin-bottom: 1.5rem;
            padding-left: 2rem;
          }
          
          .blog-content li {
            margin-bottom: 0.75rem;
          }
          
          .blog-content strong {
            font-weight: 600;
            color: #000;
          }
          
          .blog-content a {
            color: #000;
            text-decoration: underline;
          }
          
          .blog-content a:hover {
            text-decoration: none;
          }
          
          .blog-content code {
            background: #f5f5f5;
            padding: 0.2rem 0.4rem;
            border-radius: 0.25rem;
            font-family: monospace;
            font-size: 0.9em;
          }
          
          .blog-content img {
            max-width: 100%;
            height: auto;
            border-radius: 0.5rem;
            margin: 2rem 0;
          }
        `}</style>

        <Footer />
      </div>
    </>
  );
}
